// @ts-strict-ignore
import { useEffect } from "react";

import { Spacer } from "@aviary";
import { useCatalogProductQuery } from "@emerson/data/queries/CatalogProductQuery/CatalogProduct.query";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { useLocation } from "@shared/react-router-dom/react-router-dom";

import { QuickViewSingleProductDetails } from "./QuickViewSingleProductDetails";
import { SimilarProducts } from "./SimilarProducts/SimilarProducts";
import { SkeletonSingleProductDetails } from "./SkeletonSingleProductDetails";
import { useRefElement } from "./UseRefElement/useRefElement";

interface Props {
  productId: string;
}

const SingleProductDetails = ({ productId }: Props) => {
  const { isUnauthenticated } = useSharedGlobalConfig();

  const { similarProductsRef, similarProductsCallback } = useRefElement();
  const location = useLocation();
  const shouldScroll = location.state?.ifViewSimilarProduct as boolean;

  useEffect(() => {
    if (shouldScroll && similarProductsRef !== null) {
      window?.scrollTo({ top: similarProductsRef.offsetTop, behavior: "smooth" });
    }
  }, [similarProductsRef]);

  const { loading, data } = useCatalogProductQuery({
    variables: {
      productId,
      filters: {
        size: "LARGE",
      },
      isUnauthenticated,
    },
  });

  const product = data?.viewer?.product;

  if (loading) {
    return <SkeletonSingleProductDetails />;
  }

  if (!loading && !product) return null;

  const handleSimilarProductsClick = () =>
    window?.scrollTo({ top: similarProductsRef.offsetTop, behavior: "smooth" });

  const renderSimilarProducts = () => (
    <SimilarProducts productId={productId} similarProductsRef={similarProductsCallback} />
  );

  return (
    <QuickViewSingleProductDetails
      productId={productId}
      isQuickView={false}
      handleSimilarProductsClick={handleSimilarProductsClick}
    >
      <>
        <Spacer height="quad" />

        {renderSimilarProducts()}
      </>
    </QuickViewSingleProductDetails>
  );
};

export { SingleProductDetails };
